import { DRIVE_FOLDER_URL } from '@dis/gapi';
import { BaseData, parseJsonScalar, toBoolean } from './_global';
import { Dimensionals } from './dimensionals';
import { ComponentShipment } from './shipment';
import { ComponentConnection, Connections } from './connection';

export interface Asset {
  // Owner company name
  owner: string;
  // Related DIS PO that the asset was purchased under
  purchasedPO: string;
  // Related DIS Quote that the asset was purchased under
  purchasedQuote: string;
  // Write Off info
  writeOff: { isTrue: boolean, date: Date };
  // Buy Back Info
  buyBack: { isTrue: boolean, date: Date };
  // Transferred info for previous and next customer
  transferredFrom: string;
  transferredTo: string;
}

export interface ComponentShopProperties {
  shopToolBoolean: boolean;
  testConnectorBoolean: boolean;
  testConnectorRelatedSeals: [{
    partNumber: string;
    description: string;
  }];
}

export interface ComponentNdeStatus {
  lastNdeDate: Date | null;
  sixMonthInspectionDate: Date | null;
  inspectionLessThan2Weeks: boolean;
  isExpried: boolean;
}

const NDE_WINDOW_DAYS = 182.5;
const TWO_WEEKS_MS = 1209600000;

/**
 * Derives NDE (non-destructive examination) inspection status from a component's last NDE
 * date. Pulled out of the Component class so it can be shared by consumers (e.g. NdeDatesComponent)
 * that only have the raw lastNdeDate field, not a full Component instance.
 */
export function getComponentNdeStatus(lastNdeDate: Date | string | null | undefined): ComponentNdeStatus {
  const parsedLastNdeDate = lastNdeDate ? new Date(lastNdeDate) : null;

  const sixMonthInspectionDate = parsedLastNdeDate ? new Date(parsedLastNdeDate) : null;
  sixMonthInspectionDate?.setDate(sixMonthInspectionDate.getDate() + NDE_WINDOW_DAYS);

  let isExpried = false;
  if (sixMonthInspectionDate) {
    const nextNdeDate = new Date(sixMonthInspectionDate);
    nextNdeDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    isExpried = nextNdeDate.getTime() <= today.getTime();
  }

  const inspectionLessThan2Weeks =
    !isExpried && !!sixMonthInspectionDate && sixMonthInspectionDate.getTime() - Date.now() < TWO_WEEKS_MS;

  return { lastNdeDate: parsedLastNdeDate, sixMonthInspectionDate, inspectionLessThan2Weeks, isExpried };
}

export interface Component extends BaseData {
  uid: string | null;
  serialNumber: string | null;
  assemblyNumber: string | null;
  partNumber: string | null;
  toolType: string | null;
  description: string | null;
  asset: Asset | null;
  connections: Connections | null;
  dimensionals: Dimensionals | null;
  /**
   * @deprecated use dimmensionals instead;
   */
  weight: number;
  shipments: ComponentShipment[] | null;
  shopProperties: ComponentShopProperties | null;
  topConnection: ComponentConnection | null;
  bottomConnection: ComponentConnection | null;
  drawingNumber: string | null;
  /**
   * @deprecated use drawingNumber instead;
   */
  drawingRevision: string | null;
  /**
   * @deprecated use drawingNumber instead;
   */
  drawingType: string | null;
  disPoNumber: string | null;
  traceNumber: string | null;
  traceUrl: string | null;
  folderId: string | null;
  lastNde: string | null;
  lastNdeDate: Date | null;
  lastNdeInspection: Date | null;
  inventoryStatus: string | null;
  lotNumberBoolean: boolean | null;
  lotQty: number | null;
  orderedQty: number;
  status: string | null;
  latestAssignedBuild: string | null;
  rack: string | null;
  shelf: string | null;
  slot: string | null;
  bin: string | null;
  container: string | null;
  statuses: componentStatuses | null;
}

export class Component extends BaseData {
  constructor(data: Partial<Component>) {
    super(data);
    this.uid = data.serialNumber || null;
    this.serialNumber = data.serialNumber || null;
    this.assemblyNumber = data.assemblyNumber || null;
    this.partNumber = data.partNumber || null;
    this.toolType = data.toolType || null;
    this.description = data.description || null;
    this.asset = data.asset || null;
    this.dimensionals = data.dimensionals || null;
    this.shipments = data.shipments || null;
    this.shopProperties = data.shopProperties || null;
    this.topConnection = data.topConnection || null;
    this.bottomConnection = data.bottomConnection || null;
    this.drawingNumber = data.drawingNumber || null;
    this.drawingRevision = data.drawingRevision || null;
    this.drawingType = data.drawingType || null;
    this.disPoNumber = data.disPoNumber || null;
    this.traceNumber = data.traceNumber || null;
    this.traceUrl = data.traceUrl || null;
    this.inventoryStatus = data.inventoryStatus || null;
    this.folderId = data.folderId || null;
    this.lastNde = data.lastNde || null;
    this.lastNdeDate = data.lastNdeDate || null;
    this.lastNdeInspection = data.lastNdeInspection || null;
    this.lotQty = data.lotQty || null;

    // TODO: Temp fix for now
    this.lotNumberBoolean = typeof data.lotNumberBoolean === 'string' ?
      data.lotNumberBoolean === "TRUE" ? true : false
      : typeof data.lotNumberBoolean === 'boolean' ? data.lotNumberBoolean : null;
    // this.lotNumberBoolean = toBoolean(data.lotNumberBoolean);

    this.latestAssignedBuild = data.latestAssignedBuild || null;
    this.rack = data.rack || null;
    this.shelf = data.shelf || null;
    this.slot = data.slot || null;
    this.bin = data.bin || null;
    this.container = data.container || null;
    this.statuses = data.statuses || null;

    this.connections = data.connections || null;

    this.fromSpreadsheet(data);
  }

  /**
   * Create model for handling NDE properties
   */
  public get nde() {
    return getComponentNdeStatus(this.lastNdeInspection ?? this.lastNdeDate);
  }

  public get folderUrl() {
    return `${DRIVE_FOLDER_URL}${this.folderId}`;
  }

  private fromSpreadsheet(component: Partial<Component>) {
    if (component.asset && typeof component.asset === 'string') {
      this.asset = parseJsonScalar(component.asset);
    }

    if (component.dimensionals && typeof component.dimensionals === 'string') {
      this.dimensionals = parseJsonScalar(component.dimensionals);
    }

    if (component.connections && typeof component.connections === 'string') {
      this.connections = parseJsonScalar(component.connections);
    }

    if (component.shipments && typeof component.shipments === 'string') {
      this.shipments = parseJsonScalar(component.shipments);
    }

    if (component.shopProperties && typeof component.shopProperties === 'string') {
      this.shopProperties = parseJsonScalar(component.shopProperties);
    }

    if (component.statuses && typeof component.statuses === 'string') {
      this.statuses = parseJsonScalar(component.statuses);
    }

  }

  static parseConnectionString(connection: string): ComponentConnection {
    // let clonedConnection = connection.toUpperCase().replace(/W\//g, '').trim();
    let clonedConnection = connection.toUpperCase().trim();

    const obj: ComponentConnection = {
      connection,
      type: null,
      thread: 'RH',
      box: false,
      pin: false,
      bbb: false,
      srg: false,
      makeupTorque: 5000, // Default value for makeupTorque
      torqueRequired: true,
    };

    // Set box or pin;
    if (clonedConnection.includes('BOX')) {
      obj.box = true;
      clonedConnection = clonedConnection.replace('BOX', '').trim();
    } else if (clonedConnection.includes('PIN')) {
      obj.pin = true;
      clonedConnection = clonedConnection.replace('PIN', '').trim();
    }

    // Set SRG;
    if (clonedConnection.includes('SRG')) {
      obj.srg = true;
      clonedConnection = clonedConnection.replace('SRG', '').trim();
    }
    // Set BBB;
    if (clonedConnection.includes('BB') || clonedConnection.includes('BOREBACK') || clonedConnection.includes('BORE BACK')) {
      obj.bbb = true;
      clonedConnection = clonedConnection.replace(/BB|BOREBACK|BORE BACK/g, '').trim();
    }

    // Set thread (RH or LH)
    if (clonedConnection.includes('RH')) {
      obj.thread = 'RH';
      clonedConnection = clonedConnection.replace('RH', '').trim();
    } else if (clonedConnection.includes('LH')) {
      obj.thread = 'LH';
      clonedConnection = clonedConnection.replace('LH', '').trim();
    }

    // Set type
    obj.type = clonedConnection.replace(/(WITH|W\/|W)/g, '').trim();

    return obj;
  }

  static toSpreadsheet(component: Component) {
    //@ts-expect-error
    component.asset = JSON.stringify(component.asset);
    //@ts-expect-error
    component.dimensionals = JSON.stringify(component.dimensionals);
    //@ts-expect-error
    component.connections = JSON.stringify(component.connections);
    //@ts-expect-error
    component.shipments = JSON.stringify(component.shipments);
    //@ts-expect-error
    component.shopProperties = JSON.stringify(component.shopProperties);
    //@ts-expect-error
    component.statuses = JSON.stringify(component.statuses);

    return component;
  }

  /**
   * @deprecated use folderUrl instead;
   */
  public get componentFolderUrl() {
    return this.folderUrl;
  }

  public get title() {
    return this.serialNumber;
  }
}

export interface componentStatuses {
  inventoryStatus: COMPONENT_INV_STATUS;
  shipmentStatus: COMPONENT_SHIPMENT_STATUS;
  componentStatus: COMPONENT_COMP_STATUS;
  ndeStatus: COMPONENT_NDE_STATUS;
  status: string;
}

export enum COMPONENT_INV_STATUS {
  NONE = "NONE",
  AVAILABLE = "AVAILABLE",
  NOT_AVAILABLE = "NOT AVAILABLE",
  ALL_USED = "ALL USED",
}

export enum COMPONENT_COMP_STATUS {
  NONE = "NONE",
  ORDERED = "ORDERED",
  MANUFACTORING = "MANUFACTORING",
  THREADING = "THREADING",
  MANUFACTURED = "MANUFACTURED",
  RECEIVED = "RECEIVED",
  PARTIAL_RECEIVED = "PARTIAL RECEIVED",
  DAMAGED = "DAMAGED",
  SCRAP = "SCRAP",
  DOWNHOLE = "DOWNHOLE",
  LOST = "LOST",
  IN_CUST_POS = "IN CUST POS",
  REWORK = "REWORK",
  PARTIAL_REWORK = "PARTIAL REWORK",
  REISSUE = "REISSUE",
  VOID = "VOID",
  RETURNED = "RETURNED",
  NEEDS_INSPECTION = "NEEDS INSPECTION",
  BEING_INSPECTED = "BEING INSPECTED",
  INSPECTED = "INSPECTED"
}

export enum COMPONENT_SHIPMENT_STATUS {
  NONE = "NONE",
  VENDOR = "VENDOR",
  STAGED_BUILDS = "STAGED BUILDS",
  ASSIGNED_TO_JOB = "ASSIGNED TO JOB",
  // Build's line item is on a shipment's manifest, but the shipment hasn't actually left yet
  // (shipment status is still FULFILLING/RTS) — distinct from SHIPPED below.
  ASSIGNED_TO_SHIPMENT = "ASSIGNED TO SHIPMENT",
  SHIPPED = "SHIPPED",
  RECEIVED = "RECEIVED",
  RERELEASE = "RERELEASE"
}

export enum COMPONENT_NDE_STATUS {
  NONE = "NONE",
  NONCONFORMING = "NONCONFORMING",
  CONFORMING = "CONFORMING",
}

export const COMPONENT_ICON = {
  COMPONENT: 'component_exchange',
  DEPRECATED: 'disabled_by_default',
}
